import { HttpClient } from '../common/httpClient';
import { GetViberPricing, PostViberPricing, ViberCountry, ViberMonthlyFee } from '../../types/viber/viberPricing';

export interface GetViberPricingResponse {
  readonly id: number;
  readonly accountId: string;
  readonly country: { name: string, callingCode: string };
  readonly promotionalRate: number;
  readonly transactionalRate: number;
  readonly otpRate: number;
  readonly sessionsRate: number;
  readonly internationalRate?: number;
  readonly monthlyFee: number;
  readonly startDate: string | Date;
  readonly endDate: string | Date;
  readonly periodCategory: 'FUTURE' | 'CURRENT' | 'PAST';
  readonly pricingType?: string;
  readonly updateReason?: string;
}

type ViberMonthlyFeeResponse = {
  readonly id: number;
  readonly accountId?: string;
  readonly monthlyFee: number;
};

type ViberCountryResponse = {
  readonly dialCode: string;
  readonly name: string;
  readonly code: string;
};

export class ViberPricingApi {
  constructor(private readonly httpClient: HttpClient) { }

  private mapToViberPricing(response: GetViberPricingResponse): GetViberPricing {
    return {
      id: response.id,
      accountId: response.accountId,
      countryCode: response.country.callingCode!,
      countryName: response.country.name,
      promotionalRate: response.promotionalRate,
      transactionalRate: response.transactionalRate,
      otpRate: response.otpRate,
      sessionsRate: response.sessionsRate,
      internationalRate: response.internationalRate!,
      monthlyFee: response.monthlyFee,
      startDate: response.startDate,
      endDate: response.endDate!,
      periodCategory: response.periodCategory,
      pricingType: response.pricingType,
      updateReason: response.updateReason,
    };
  }

  private mapToViberMonthlyFee(response: ViberMonthlyFeeResponse): ViberMonthlyFee {
    return {
      id: response.id,
      accountId: response.accountId,
      monthlyFee: response.monthlyFee,
    };
  }

  private mapToViberCountry(response: ViberCountryResponse): ViberCountry {
    return {
      dialCode: response.dialCode,
      name: response.name,
      code: response.code,
    };
  }

  getPricing(tenantId: string): Promise<GetViberPricing[]> {
    return this.httpClient.get<GetViberPricingResponse[]>(`pricing/${tenantId}`)
      .then(data => (
        data.map(it => this.mapToViberPricing(it))
      ));
  }

  getMonthlyFee(tenantId: string): Promise<ViberMonthlyFee> {
    return this.httpClient.get<ViberMonthlyFeeResponse>(`monthly-fee/${tenantId}`)
      .then(data => this.mapToViberMonthlyFee(data));
  }

  getAll(tenantId: string): Promise<GetViberPricing[]> {
    return this.httpClient.get<GetViberPricingResponse[]>(`pricing/${tenantId}`)
      .then(data => (
        data.map(it => this.mapToViberPricing(it))
      ));
  }

  getCountries(): Promise<ViberCountry[]> {
    return this.httpClient.get<ViberCountry[]>(`price-list/countries`)
      .then(data => (
        data.map(it => this.mapToViberCountry(it))
      ));
  }

  async addMonthlyFee(accountId: string, fee: number): Promise<void> {
    await this.httpClient.post<ViberMonthlyFee>(`monthly-fee/${accountId}`, { accountId: accountId, monthlyFee: fee });
  }

  async updateMonthlyFee(accountId: string, id: number, fee: number): Promise<void> {
    await this.httpClient.put<ViberMonthlyFee>(`monthly-fee/${accountId}/${id}`, { accountId: accountId, monthlyFee: fee });
  }

  async add(tenantId: string, values: Partial<PostViberPricing>): Promise<void> {
    await this.httpClient.post<GetViberPricing>(`pricing/${tenantId}`, values);
  }

  async update(tenantId: string, id: number, item: PostViberPricing): Promise<void> {
    await this.httpClient.put<PostViberPricing>(`pricing/${tenantId}/${id}`, item);
  }

  async delete(tenantId: string, id: number): Promise<void> {
    await this.httpClient.delete<GetViberPricing>(`pricing/${tenantId}/${id}`);
  }
}
